import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { z } from "zod/v4";

import { normalizeErrorMessages } from "api/common/utils/rtkq.utils";
import {
  useUpdateMailUserMutation,
  useDeleteMailUserMutation,
  useListMailListsQuery,
} from "api/memberPortal/mailList/mailList.api";
import { MailAccount } from "api/memberPortal/mailList/mailList.types";
import { useAppForm } from "components/Forms/AppForm";
import DeleteModal from "components/MUI/DeleteModal/DeleteModal";
import { pluralize } from "utils/stringFormatting";

import SideDetailPanel from "./components/SideDetailPanel";
import MailListMembersList from "./MailListMembersList.list";

interface MailListMembersDrawerProps {
  member: MailAccount | undefined;
}

const MembersEditSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.email().min(1, "Email is required"),
  mailListIds: z.array(z.number()),
});

export default function MailListMembersDrawer({
  member,
}: MailListMembersDrawerProps) {
  const [editMode, setEditMode] = useState<boolean>(false);
  const [updateUser, { isLoading, error, reset: resetUpdateError }] =
    useUpdateMailUserMutation();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [deleteUser] = useDeleteMailUserMutation();
  const { data: mailLists } = useListMailListsQuery({});

  const handleDisableEditMode = () => {
    EditMemberForm.reset();
    resetUpdateError();
    setEditMode(false);
  };

  const EditMemberForm = useAppForm({
    defaultValues: {
      name: member?.name ?? "",
      email: member?.email ?? "",
      mailListIds: member?.mailingLists.map((ml) => ml.id) ?? [],
    },

    validators: {
      onSubmit: MembersEditSchema,
    },

    onSubmit: async ({ value }) => {
      if (!member) {
        return;
      }

      try {
        // Update name/email (+ list membership). .unwrap() throws on a 4xx so
        // a rejected change — e.g. an email edit refused because the account is
        // a real Microsoft mailbox, not a mail contact we own — leaves the form
        // in edit mode with the entered values, and the error surfaces below.
        const updated = await updateUser({
          ...value,
          id: member.id,
        }).unwrap();

        const { mailListIds } = value;
        EditMemberForm.reset({
          name: updated.name,
          email: updated.email,
          mailListIds,
        });
        handleDisableEditMode();
      } catch {
        // Error state is rendered from the mutation's `error` (see the Alert).
      }
    },
  });

  const handleEnableEditMode = () => {
    setEditMode(true);
  };

  const handleDeleteUserModal = () => {
    setOpenModal(true);
  };
  const handleDeleteUser = async () => {
    if (!member) return;
    await deleteUser(member.id);
    setOpenModal(false);
    handleDisableEditMode();
  };

  if (!member) {
    return (
      <SideDetailPanel sx={{ backgroundColor: "glyph.light" }}>
        <Stack
          sx={{
            height: "100%",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            backgroundColor: "glyph.light",
            borderRadius: 1,
            p: 3,
          }}
        >
          <PersonSearchIcon sx={{ fontSize: 48, color: "ink.light", mb: 1 }} />
          <Typography variant="h6" sx={{ mb: 1 }} color="secondary">
            No member selected
          </Typography>
          <Typography color="secondary" variant="subtitle2">
            Select a member from the table to see their details and mailing
            lists.
          </Typography>
        </Stack>
      </SideDetailPanel>
    );
  }

  return (
    <SideDetailPanel>
      <Stack>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            EditMemberForm.handleSubmit();
          }}
        >
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Typography variant="overline" sx={{ mb: 3, fontSize: 15 }}>
              Member Details
            </Typography>

            <Stack direction="row" spacing={0.5}>
              {editMode && (
                <IconButton
                  size="small"
                  onClick={() => {
                    EditMemberForm.reset();
                    handleDisableEditMode();
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              )}
              <IconButton
                size="small"
                onClick={
                  editMode
                    ? () => EditMemberForm.handleSubmit()
                    : handleEnableEditMode
                }
              >
                {editMode ? (
                  <SaveIcon fontSize="inherit" />
                ) : (
                  <EditIcon fontSize="inherit" />
                )}
              </IconButton>
            </Stack>
          </Stack>

          <Stack spacing={1.5} sx={{ mb: 3 }}>
            {editMode ? (
              <>
                <EditMemberForm.AppField name="name">
                  {(field) => <field.TextInput label="Name" required />}
                </EditMemberForm.AppField>
                <EditMemberForm.AppField name="email">
                  {(field) => <field.TextInput label="Email" required />}
                </EditMemberForm.AppField>
                {error && (
                  <Alert severity="error" sx={{ py: 0 }}>
                    {normalizeErrorMessages(error)}
                  </Alert>
                )}
              </>
            ) : (
              <>
                <Typography variant="h6" noWrap>
                  {member.name}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{ mb: 3 }}
                >
                  <MailOutlineIcon fontSize="inherit" color="action" />
                  <Link
                    href={`mailto:${member.email}`}
                    variant="body2"
                    underline="hover"
                    noWrap
                  >
                    {member.email}
                  </Link>
                </Stack>
              </>
            )}
          </Stack>

          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {(member.mailingListCount > 0 || editMode) && !isLoading ? (
              <EditMemberForm.AppField name="mailListIds">
                {(field) => (
                  <MailListMembersList
                    mailLists={
                      editMode ? (mailLists ?? []) : member.mailingLists
                    }
                    enableSelection={editMode}
                    selectedIds={field.state.value}
                    onSelectionChange={(ids) => field.handleChange(ids)}
                  />
                )}
              </EditMemberForm.AppField>
            ) : isLoading ? (
              <CircularProgress />
            ) : (
              <Typography>No Mailing Lists</Typography>
            )}
          </Box>
          {editMode && (
            <Box
              sx={{ my: 2, display: "flex", justifyContent: "space-between" }}
            >
              <Button
                color="error"
                variant="contained"
                onClick={handleDeleteUserModal}
              >
                Delete User
              </Button>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="outlined" onClick={handleDisableEditMode}>
                  Cancel
                </Button>
                <Button variant="contained" type="submit">
                  Save User
                </Button>
              </Box>
            </Box>
          )}
        </form>
        <DeleteModal
          open={openModal}
          name={"User"}
          onClose={() => setOpenModal(false)}
          onDelete={handleDeleteUser}
        >
          <Card
            sx={{
              p: 2,
            }}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              {member.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {member.email}
            </Typography>
            {member.mailingListCount > 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Removing from {`${pluralize(member.mailingListCount, "list")}`}
              </Typography>
            )}
          </Card>
        </DeleteModal>
      </Stack>
    </SideDetailPanel>
  );
}
